// A smelter, described the way age-of-mcp authors it: a `furnace` helper plus the
// shared `foundationSlab` and `scaffold`, walked through its build stages. Ported
// 1:1 from age-of-mcp's smelter.ts.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, benchWork, smokePlume, firePulse, EFFECT_LOOP, type Part } from "../../lib";

const STONE = "#6f6f74";   // furnace stone
const DARK  = "#4a4a4e";   // darker trim
const FIRE  = "#f0842c";   // glowing mouth
const EMBER = "#ffd24a";   // bright embers
const SMOKE = "#b8b8b8";
const IRON  = "#9aa0a6";   // an ingot out front

// A stout stone furnace body, `h` tiles tall, on the foundation.
function furnace(h: number): Part {
  return { geometry: new THREE.BoxGeometry(0.66, h, 0.66), color: STONE, position: [0, 0.14 + h / 2, 0] };
}

// The "furnace is running" cue: smoke rising from the flue mouth (the flue tops
// out at y = 1.39) while the embers in the fire mouth pulse.
const plume = smokePlume([-0.18, 1.39, -0.18], { rise: 0.42, radius: 0.07, color: SMOKE });

// The build stages of the smelter: foundation, body with scaffolding, taller
// body, and the finished furnace with cap, flue, glowing mouth and ingot.
function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), furnace(0.3), ...scaffold(0.82, 0.6)];
  if (s === 2) return [foundationSlab(0.84), furnace(0.6), ...scaffold(0.82, 1.05)];

  const bodyH = 0.7, top = 0.14 + bodyH;
  return [
    foundationSlab(0.84),
    furnace(bodyH),
    // A battered stone cap on the furnace.
    { geometry: new THREE.BoxGeometry(0.74, 0.1, 0.74), color: DARK, position: [0, top + 0.05, 0] },
    // A tall flue with rising smoke (the puffs the "active" animation drives).
    { geometry: new THREE.BoxGeometry(0.22, 0.5, 0.22), color: STONE, position: [-0.18, top + 0.3, -0.18] },
    ...plume.parts,
    // A glowing fire mouth on the front, with bright embers.
    { geometry: new THREE.BoxGeometry(0.3, 0.22, 0.06), color: "#201a16", position: [0, 0.14 + 0.26, 0.34] },
    { geometry: new THREE.BoxGeometry(0.24, 0.16, 0.05), color: FIRE,    position: [0, 0.14 + 0.26, 0.36] },
    { geometry: new THREE.BoxGeometry(0.12, 0.08, 0.05), color: EMBER,   position: [0, 0.14 + 0.22, 0.37], name: "embers" },
    // A small bellows / stone lip beside the mouth.
    { geometry: new THREE.BoxGeometry(0.16, 0.1, 0.2), color: DARK, position: [0.4, 0.14 + 0.1, 0.28] },
    // A fresh iron ingot cooling out front.
    { geometry: new THREE.BoxGeometry(0.2, 0.07, 0.12), color: IRON, position: [0.34, 0.14 + 0.035, 0.5] },
  ];
}

export const smelter = defineModel({
  id: "smelter",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Smelter",
  tags: ["building","smelter","low-poly","age-of-mcp","classic","animated"],
  summary: "Low-poly smelter.",
  description: "A stone furnace that smelts ore into iron ingots.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Furnace burning",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [...plume.tracks, firePulse("embers", [0, 0.14 + 0.22, 0.37])],
    },
  ],
  stations: [
    { id: "front", position: [0, 0.14, 0.42], facing: Math.PI, description: "Where a smith tends the furnace." },
  ],
  interactions: [
    { id: "smelt", label: "Tending the furnace", station: "front", variants: ["complete"], ...benchWork() },
  ],
});
